import React, { useState } from "react";
import { ShieldCheck, Search, CheckCircle2, XCircle, Loader2, AlertTriangle, Trash2, FileDown } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";

export default function DeletionVerify() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (query.trim().length < 2) { toast.error("Enter an email or full name (min 2 characters)"); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/deletion-verify?query=${encodeURIComponent(query.trim())}`);
      setResult(data);
    } catch (e) { toast.error(formatApiError(e)); setResult(null); }
    finally { setLoading(false); }
  };

  const purge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post(`/admin/deletion-verify/purge`, { query: query.trim() });
      setResult(data);
      const p = data.purged || {};
      if (data.all_clear) toast.success(`Purged ${p.accounts || 0} account(s), ${p.records || 0} record(s) — now fully clear`);
      else toast.warning("Some records still remain after purge — review below");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPurging(false); }
  };

  const downloadCertificate = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/admin/deletion-verify/certificate?query=${encodeURIComponent(query.trim())}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `erasure-certificate-${query.trim().replace(/[^A-Za-z0-9_.-]+/g, "_").slice(0, 60)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Certificate downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDownloading(false); }
  };

  return (
    <div>
      <PageHeader
        title="Deletion Verification"
        subtitle="Confirm an executed deletion left nothing behind. Enter a deleted account's email or full name to sweep every collection and verify zero records remain."
      />

      <Card className="mb-6 border-slate-200 p-5 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative min-w-[280px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && run()}
              placeholder="deleted.user@email.com or Jane Doe"
              className="pl-9"
              data-testid="deletion-verify-input"
            />
          </div>
          <Button className="rounded-full" onClick={run} disabled={loading} data-testid="deletion-verify-run">
            {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1.5 h-4 w-4" />}
            Run Sweep
          </Button>
        </div>
      </Card>

      {result && (
        <div data-testid="deletion-verify-result">
          <div
            className={`mb-4 flex items-center gap-3 rounded-xl border p-4 ${
              result.all_clear ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"
            }`}
            data-testid="deletion-verify-summary"
          >
            {result.all_clear ? (
              <CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-600" />
            ) : (
              <AlertTriangle className="h-6 w-6 shrink-0 text-red-600" />
            )}
            <div>
              <p className={`text-base font-bold ${result.all_clear ? "text-emerald-800" : "text-red-800"}`}>
                {result.all_clear ? "Fully purged — zero records remain" : "Residual data found — deletion incomplete"}
              </p>
              <p className="text-sm text-slate-600">
                Query: <span className="font-medium">{result.query}</span>
                {" · "}
                {result.matched_id_count > 0
                  ? `${result.matched_id_count} account id(s) resolved`
                  : "no account id resolved (searched by email/name)"}
              </p>
            </div>
            {!result.all_clear && (
              <Button
                variant="destructive"
                className="ml-auto rounded-full"
                onClick={purge}
                disabled={purging}
                data-testid="deletion-verify-purge"
              >
                {purging ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
                Purge remaining &amp; re-verify
              </Button>
            )}
            {result.all_clear && (
              <Button
                variant="outline"
                className="ml-auto rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                onClick={downloadCertificate}
                disabled={downloading}
                data-testid="deletion-verify-certificate"
              >
                {downloading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileDown className="mr-1.5 h-4 w-4" />}
                Download certificate
              </Button>
            )}
          </div>

          <Card className="border-slate-200 shadow-sm">
            <ul className="divide-y divide-slate-100" data-testid="deletion-verify-checklist">
              {result.checks.map((c) => (
                <li key={c.key} className="flex items-center justify-between px-5 py-3.5" data-testid={`deletion-verify-check-${c.key}`}>
                  <div className="flex items-center gap-3">
                    {c.status === "pass" ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                    <span className="text-sm font-medium text-slate-800">{c.collection}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-500">{c.remaining} record{c.remaining === 1 ? "" : "s"}</span>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide ${
                        c.status === "pass" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                      }`}
                      data-testid={`deletion-verify-status-${c.key}`}
                    >
                      {c.status}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
    </div>
  );
}
